import React, { createContext, useContext, useEffect, useState } from 'react'
import { api } from '@/lib/api'

export interface AuthUser {
  id: number
  name: string
  email: string
  rut?: string
  iniciales: string
  is_super_admin: boolean
}

export interface WebSession {
  empresa: { id: number; nombre: string; rut: string } | null
  token: string
  token_type: 'Bearer'
  is_super_admin?: boolean
}

interface LoginResponse {
  usuario: AuthUser
  sessions: WebSession[]
}

interface UserContextType {
  user: AuthUser | null
  isLoading: boolean
  login: (rut: string, password: string) => Promise<void>
  logout: () => Promise<void>
  refreshUser: () => Promise<void>
}

const UserContext = createContext<UserContextType | undefined>(undefined)

const NOT_ADMIN_MESSAGE = 'Esta cuenta no tiene acceso al panel de administración.'

class NotSuperAdminError extends Error {
  constructor() {
    super(NOT_ADMIN_MESSAGE)
    this.name = 'NotSuperAdminError'
  }
}

async function silentLogout() {
  try {
    await api.post('/auth/logout')
  } catch {
    /* ignore */
  } finally {
    localStorage.removeItem('auth_token')
  }
}

export function UserProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const token = localStorage.getItem('auth_token')
    if (!token) {
      setIsLoading(false)
      return
    }

    api
      .get<{ data: AuthUser }>('/auth/user')
      .then((res) => {
        if (res.data?.is_super_admin === true) {
          setUser(res.data)
        } else {
          localStorage.removeItem('auth_token')
        }
      })
      .catch(() => localStorage.removeItem('auth_token'))
      .finally(() => setIsLoading(false))
  }, [])

  useEffect(() => {
    const handleForceLogout = () => {
      setUser(null)
    }
    window.addEventListener('auth:logout', handleForceLogout)
    return () => window.removeEventListener('auth:logout', handleForceLogout)
  }, [])

  const login = async (rut: string, password: string) => {
    // El panel admin usa RUT como credencial canónica (igual que fc-web-app),
    // no email. Endpoint dedicado: /auth/admin/login valida por RUT y, si el
    // usuario es super-admin, devuelve una única sesión sintética sin empresa.
    // Si NO es super-admin, el backend responde 403 (sin acuñar token) y
    // api.post lanza antes de llegar acá — la guarda de abajo queda como
    // defensa redundante para respuestas inesperadas.
    const res = await api.post<LoginResponse>('/auth/admin/login', { rut, password })
    const session = res.sessions?.[0]

    if (res.usuario?.is_super_admin !== true || !session) {
      // No es super-admin (o respuesta inesperada): revocar el token recién
      // emitido antes de rechazar.
      if (session) {
        localStorage.setItem('auth_token', session.token)
        await silentLogout()
      }
      setUser(null)
      throw new NotSuperAdminError()
    }

    localStorage.setItem('auth_token', session.token)
    // Hidratar el usuario completo desde /auth/user — el payload de login no
    // incluye `iniciales`.
    const me = await api.get<{ data: AuthUser }>('/auth/user')
    setUser(me.data)
  }

  const logout = async () => {
    try {
      await api.post('/auth/logout')
    } finally {
      localStorage.removeItem('auth_token')
      setUser(null)
    }
  }

  const refreshUser = async () => {
    const res = await api.get<{ data: AuthUser }>('/auth/user')
    if (res.data?.is_super_admin !== true) {
      await silentLogout()
      setUser(null)
      throw new NotSuperAdminError()
    }
    setUser(res.data)
  }

  return (
    <UserContext.Provider value={{ user, isLoading, login, logout, refreshUser }}>
      {children}
    </UserContext.Provider>
  )
}

export function useUser() {
  const context = useContext(UserContext)
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider')
  }
  return context
}

export { NotSuperAdminError }
